import axios from 'axios';
import {
  CareerFrameworksService,
  DropboxerService,
  LoginService,
  OpenAiService,
  UserService,
} from 'client';
import {OpenAPI} from 'client/core/OpenAPI';
import {TFunction} from 'i18next';

// Enforce a single authoritative is_sandbox value on every outgoing request.
//
// The generated OpenAPI client (request.ts) bakes query params directly into the URL
// string via getQueryString(), so is_sandbox=false ends up in config.url — not in
// config.params. A normal axios.defaults.params approach therefore produces a duplicate
// (is_sandbox=false from the URL + is_sandbox=true from defaults). This interceptor
// strips is_sandbox from both the URL string and config.params, then re-adds it once
// with the correct value.
axios.interceptors.request.use((config) => {
  try {
    const flag =
      typeof window !== 'undefined'
        ? window.localStorage.getItem('sandbox_mode')
        : null;
    const isSandbox = flag === 'true';

    // Strip is_sandbox from the URL query string (set by the generated client).
    let cleanUrl = config.url;
    if (cleanUrl) {
      const qIdx = cleanUrl.indexOf('?');
      if (qIdx !== -1) {
        const base = cleanUrl.slice(0, qIdx);
        const search = new URLSearchParams(cleanUrl.slice(qIdx + 1));
        search.delete('is_sandbox');
        const newQuery = search.toString();
        cleanUrl = newQuery ? `${base}?${newQuery}` : base;
      }
    }

    // Strip is_sandbox from config.params (set by any direct axios callers).
    const params = config.params
      ? {...(config.params as Record<string, unknown>)}
      : {};
    delete params.is_sandbox;

    // Re-add a single correct value only in sandbox mode.
    if (isSandbox) {
      params.is_sandbox = true;
    }

    return {
      ...config,
      url: cleanUrl,
      params: Object.keys(params).length ? params : undefined,
    };
  } catch {
    return config;
  }
});

const configOpenApi = () => {
  OpenAPI.BASE = process.env.REACT_APP_API_URL ?? '';
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
};

const ORIGIN = window.location.origin;
export const API_URL = process.env.REACT_APP_API_URL ?? '';
export const LOGIN_URL = `${API_URL}/api/v1/auth/login?redirect_uri=${ORIGIN}`;
export const LOGOUT_URL = `${API_URL}/api/v1/auth/logout?post_logout_redirect_uri=${ORIGIN}`;

export const pluralize = (
  count: number,
  copy: string,
  t: TFunction<'translation', undefined, 'translation'>
) => {
  return count === 1
    ? t(copy, {
        count: count,
      })
    : t(copy + '_plural', {
        count: count,
      });
};

export const nameToInitials = (name: string) => {
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

export const getAuthService = () => {
  configOpenApi();
  return UserService;
};

export const getLoginService = () => {
  configOpenApi();
  return LoginService;
};

export const getOpenAiService = () => {
  configOpenApi();
  return OpenAiService;
};

export const getDropboxService = () => {
  configOpenApi();
  return DropboxerService;
};

export const getCareerFrameworksService = () => {
  configOpenApi();
  return CareerFrameworksService;
};

export const isSandboxMode = (): boolean => {
  try {
    return window.localStorage.getItem('sandbox_mode') === 'true';
  } catch {
    return false;
  }
};

export const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};
